import { queryClient } from "@/App";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import pWaitFor from "p-wait-for";
import posthog from "posthog-js";

import type { LogtoUser } from "@/types/logtoUser";
import { logtoUserQueryKey } from "@/hooks/api/useLogtoUser";
import useUser from "@/hooks/api/user/useUser";

const AuthenticatedLayout = () => {
  // Fetch user data globally so it's available for route guards
  useUser();

  return <Outlet />;
};

export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
  pendingComponent: () => (
    <div className="fixed inset-0 flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="border-gray-900 mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2"></div>
        <p className="text-gray-600">Checking authentication...</p>
      </div>
    </div>
  ),

  beforeLoad: async ({ context, location }) => {
    await pWaitFor(() => !context.auth.current.isLoading);

    // If not authenticated, go to login
    if (!context.auth.current.isAuthenticated) throw redirect({ to: "/login" });

    await pWaitFor(() => queryClient.isFetching({ queryKey: logtoUserQueryKey }) === 0);

    const user = queryClient.getQueryData<LogtoUser>(logtoUserQueryKey);
    if (!user) throw new Error("User data not found in query cache");

    if (!posthog._isIdentified()) {
      posthog.identify(user.sub, {
        email: user.email,
      });
    }

    if (user.custom_data?.suspended && location.pathname !== "/bye") throw redirect({ to: "/bye" });

    const goingToOnboarding = location.pathname === "/onboarding";
    const hasCompletedOnboarding = !!user.custom_data?.intro_completed;

    // Redirect to onboarding if not completed and not already on onboarding
    if (!hasCompletedOnboarding && !goingToOnboarding) throw redirect({ to: "/onboarding" });
  },
});
